import { useEffect, useRef } from 'react';

/* Rafraichissement automatique d'une page du CRM, sans jamais deranger l'utilisateur.
   Regles :
   - on ne recharge que si l'onglet du navigateur est VISIBLE (aucune requete inutile) ;
   - on ne recharge PAS pendant une saisie (champ de texte, liste deroulante, zone
     editable) : on ne veut pas faire sauter un formulaire en cours de remplissage ;
   - on recharge IMMEDIATEMENT au retour sur l'onglet, pour ne rien manquer ;
   - le rechargement se fait EN PLACE (la fonction de chargement de la page est
     rappelee), donc ni remontage du composant, ni perte de filtre ou de defilement.
   Usage : useRafraichissementAuto(charger, { secondes: 30, actif: !modalOuvert }) */
export function useRafraichissementAuto(rafraichir, options = {}) {
  const { secondes = 30, actif = true } = options;
  const garde = useRef(rafraichir);
  garde.current = rafraichir;

  useEffect(() => {
    if (!actif || typeof garde.current !== 'function') return undefined;

    const enSaisie = () => {
      const e = document.activeElement;
      if (!e) return false;
      const balise = (e.tagName || '').toLowerCase();
      return balise === 'input' || balise === 'textarea' || balise === 'select' || e.isContentEditable;
    };
    const possible = () => document.visibilityState === 'visible' && !enSaisie();
    const tenter = () => { if (possible()) { try { garde.current(); } catch (e) { /* on reessaiera */ } } };

    const minuteur = setInterval(tenter, Math.max(10, Number(secondes) || 30) * 1000);
    const auRetour = () => { if (document.visibilityState === 'visible') tenter(); };
    document.addEventListener('visibilitychange', auRetour);
    window.addEventListener('focus', auRetour);
    return () => {
      clearInterval(minuteur);
      document.removeEventListener('visibilitychange', auRetour);
      window.removeEventListener('focus', auRetour);
    };
  }, [secondes, actif]);
}

export default useRafraichissementAuto;
